import Box from "@mui/material/Box";
import React, { useContext, useState, useEffect } from "react";

import FilterAltIcon from "@mui/icons-material/FilterAlt";
import ClearIcon from "@mui/icons-material/Clear";
import { Badge, Button, IconButton, Tooltip, Popover, Checkbox, FormGroup, FormControlLabel, Typography, Tabs, Tab } from "@mui/material";
import { ReactSearchAutocomplete } from "react-search-autocomplete";
import { ApiDataContext } from "contexts/ApiDataContext";

export default function SearchBar({
  data,
  isMyActivitiesMarked,
  setIsMyActivitiesMarked,
  searchValue,
  setSearchValue,
  setProvidencesFilter,
  providencesFilter,
}) {
  const { apiData } = useContext(ApiDataContext);

  const handleOnSearch = (string, results) => {
    // setSearchValue(results);
  };

  const handleOnHover = (result) => {};

  const handleOnSelect = (item) => {
    setSearchValue(item);
    // Atualizar URL para abrir diretamente a atividade selecionada
    const url = new URL(window.location);
    url.searchParams.set('activityId', item.id.toString());
    url.searchParams.set('openActivity', 'true');
    url.searchParams.delete('search');
    window.history.pushState({}, '', url);
  };

  const handleOnFocus = () => {};

  const handleOnClear = () => {
    setSearchValue([]);
  };

  const handleClearAllFilters = () => {
    setSearchValue([]);
    setSelectedProvidences([]);
    setProvidencesFilter([]);
    setIsMyActivitiesMarked(true);
  };

  const handleTabChange = (event, newValue) => {
    setIsMyActivitiesMarked(newValue === 0); // 0 = "Minhas Atividades", 1 = "Todas as Atividades"
  };

  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const open = Boolean(filterAnchorEl);
  const handleOpenFilters = (event) => setFilterAnchorEl(event.currentTarget);
  const handleCloseFilters = () => setFilterAnchorEl(null);

  const [selectedProvidences, setSelectedProvidences] = useState([]);

  // Sincronizar selectedProvidences com providencesFilter (da URL/props)
  useEffect(() => {
    if (!apiData?.providences) return;
    
    if (providencesFilter && providencesFilter.length > 0) {
      const selected = apiData.providences.filter(p => providencesFilter.includes(p.id));
      setSelectedProvidences(selected);
    } else if (!providencesFilter || providencesFilter.length === 0) {
      setSelectedProvidences([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providencesFilter]); // Só depende de providencesFilter, apiData.providences é usado mas não como dependência

  const isProvidenceChecked = (selected) => {
    let isEqual = false;

    selectedProvidences.forEach((e) => {
      if (e.id === selected.id) {
        isEqual = true;
      }
    });
    return isEqual;
  };

  const handleToggleProvidence = (prov) => {
    let next;
    if (isProvidenceChecked(prov)) {
      next = selectedProvidences.filter((p) => p.id !== prov.id);
    } else {
      next = [...selectedProvidences, prov];
    }
    setSelectedProvidences(next);
    const ids = next.map((p) => p.id);
    setProvidencesFilter(ids); // aplica instantaneamente
  };

  const formatResult = (item) => {
    return (
      <div className="result-wrapper">
        <span className="result-span">
          {item.projectNumber} - {item.name}
        </span>
      </div>
    );
  };

  return (
    <React.Fragment>
      {/* Barra de busca e controles */}
      <Box display="flex" width="100%" alignItems="center" gap={2} mb={2}>
        <Box flex={1}>
          <ReactSearchAutocomplete
            items={data || []}
            maxResults={15}
            onSearch={handleOnSearch}
            onHover={handleOnHover}
            onSelect={handleOnSelect}
            onFocus={handleOnFocus}
            placeholder="Busque por nome ou numero"
            onClear={handleOnClear}
            showNoResultsText="Nenhum resultado"
            fuseOptions={{ keys: ["projectNumber", "name"] }}
            resultStringKeyName="name"
            formatResult={formatResult}
            styling={{
              zIndex: 4,
            }}
            value={searchValue}
            className="search"
          />
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Badge
            invisible={!providencesFilter || providencesFilter.length === 0}
            badgeContent={providencesFilter.length}
            color="secondary"
          >
            <Button
              sx={{ borderRadius: 2 }}
              variant="contained"
              endIcon={<FilterAltIcon />}
              onClick={handleOpenFilters}
            >
              Filtros
            </Button>
          </Badge>
          <Tooltip title="Resetar filtros">
            <IconButton
              onClick={handleClearAllFilters}
              sx={{ 
                color: 'error.main',
                borderRadius: 2,
                width: 40,
                height: 40,
                '&:hover': {
                  backgroundColor: 'error.light',
                  color: 'white'
                }
              }}
            >
              <ClearIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Sistema de Abas */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs 
          value={isMyActivitiesMarked ? 0 : 1} 
          onChange={handleTabChange}
          sx={{
            '& .MuiTabs-indicator': {
              height: 3,
            },
          }}
        >
          <Tab 
            label="Minhas Atividades" 
            sx={{ 
              fontWeight: isMyActivitiesMarked ? 700 : 500,
              fontSize: '0.95rem',
              textTransform: 'none',
            }}
          />
          <Tab 
            label="Todas as Atividades" 
            sx={{ 
              fontWeight: !isMyActivitiesMarked ? 700 : 500,
              fontSize: '0.95rem',
              textTransform: 'none',
            }}
          />
        </Tabs>
      </Box>

      <Popover
        open={open}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilters}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, minWidth: 260 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle1">Filtros</Typography>
            <Button size="small" onClick={() => { setSelectedProvidences([]); setProvidencesFilter([]); }}>Limpar</Button>
          </Box>
          <FormGroup>
            {(apiData?.providences || []).map((prov) => (
              <FormControlLabel
                key={prov.id}
                control={<Checkbox checked={isProvidenceChecked(prov)} onChange={() => handleToggleProvidence(prov)} />}
                label={prov.providenceName}
              />
            ))}
          </FormGroup>
        </Box>
      </Popover>
    </React.Fragment>
  );
}
